import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import type { Scene, MapAdapter } from '../types';

const TITILER = 'https://titiler-centinela-746476722093.us-central1.run.app';
const DEM_COG = 'gs://centinela-test-data/openclaw/elevation/cop_dem_5m_bilinear_2km_buf_cog.tif';

// Register pmtiles:// protocol with MapLibre (safe to call multiple times — checks internally)
const _proto = new Protocol();
maplibregl.addProtocol('pmtiles', _proto.tile.bind(_proto) as Parameters<typeof maplibregl.addProtocol>[1]);

// Pre-processed Mapbox terrain-RGB PMtiles (NaN areas = transparent → no spike geometry)
const PMTILES_URL =
  'pmtiles://https://storage.googleapis.com/centinela-test-data/openclaw/elevation/terrain.pmtiles';

const HILLSHADE_URL =
  `${TITILER}/cog/preview.png` +
  `?url=${encodeURIComponent(DEM_COG)}&algorithm=hillshade&max_size=600`;

export class SceneTerrain implements Scene {
  readonly panel: HTMLElement;
  private overlay: HTMLElement | null = null;
  private miniMap: maplibregl.Map | null = null;

  constructor(
    root: HTMLElement,
    _map: MapAdapter,           // kept for interface compatibility
    private readonly planetCogUri: string,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'terrain';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Topography Baseline</div>
        <div class="sm-panel-sub">COP-DEM · 5 m · Zambezi Escarpment</div>
      </div>
      <div class="sm-headline">
        The escarpment gradient determines every vegetation community in this landscape.
      </div>
      <div class="sm-panel-narrative">
        The landscape rises from the <strong>Zambezi floodplain at ~330 m</strong> in
        the north to the <strong>Mavhuradonha escarpment at ~760 m</strong> in the south.
        This 430 m gradient drives a steep rainfall gradient. The hot, arid lowlands
        (under 600 mm/yr) support Mopane and bare escarpment; the cooler highlands sustain
        Miombo and Combretum woodland. South-facing slopes retain moisture, creating
        the refugia where Riverine and Jesse Thicket communities persist.
      </div>
      <div class="sm-terrain-stat-list">
        <div class="sm-terrain-stat"><span>Min elevation</span><strong>~330 m</strong></div>
        <div class="sm-terrain-stat"><span>Max elevation</span><strong>~762 m</strong></div>
        <div class="sm-terrain-stat"><span>Relief range</span><strong>~430 m</strong></div>
        <div class="sm-terrain-stat"><span>Exaggeration</span><strong>1×</strong></div>
      </div>
      <div class="sm-terrain-hint">Drag to rotate · scroll to zoom</div>
    `;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (this.overlay) return;

    this.overlay = document.createElement('div');
    this.overlay.className = 'sm-terrain-overlay';
    this.overlay.innerHTML = `
      <div class="sm-terrain-3d-section" id="sm-terrain-3d-section">
        <div class="sm-terrain-panel-label">3D Terrain · Planet RGB · 1× vertical exaggeration</div>
        <div id="sm-terrain-minimap" class="sm-terrain-minimap"></div>
      </div>
      <div class="sm-terrain-river-section">
        <div class="sm-terrain-panel-label">Topographic Relief · Hillshade</div>
        <img class="sm-terrain-hs-img" src="${HILLSHADE_URL}" alt="hillshade" />
        <div class="sm-terrain-hs-caption">
          Valley channels guide river flow from the escarpment to the Zambezi. Darker
          tones mark steep cliff faces; lighter tones show flat, open woodland on the plateau.
        </div>
      </div>
    `;

    const app = document.getElementById('app') ?? document.body;
    app.appendChild(this.overlay);

    // Wait one frame so the container has laid out before MapLibre queries dimensions
    requestAnimationFrame(() => {
      const minimapEl = this.overlay!.querySelector<HTMLElement>('#sm-terrain-minimap')!;

      this.miniMap = new maplibregl.Map({
        container:        minimapEl,
        style:            'https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json',
        center:           [29.75, -15.9],
        zoom:             9.2,
        pitch:            55,
        bearing:          -20,
        interactive:      true,
        attributionControl: false,
      });

      this.miniMap.on('load', () => {
        // Terrain DEM from pre-processed PMtiles (transparent NaN = no spike geometry)
        this.miniMap!.addSource('terrain-dem', {
          type:     'raster-dem',
          url:      PMTILES_URL,
          tileSize: 512,
          encoding: 'mapbox',
        });
        this.miniMap!.setTerrain({ source: 'terrain-dem', exaggeration: 1 });

        // Planet monthly mosaic RGB draped on terrain surface
        if (this.planetCogUri) {
          const planetUrl =
            `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
            `?url=${encodeURIComponent(this.planetCogUri)}` +
            `&bidx=3&bidx=2&bidx=1` +
            `&rescale=500,2500&rescale=500,2500&rescale=500,2500&nodata=nan`;
          this.miniMap!.addSource('planet-drape', {
            type: 'raster', tiles: [planetUrl], tileSize: 256,
          });
          this.miniMap!.addLayer({
            id: 'planet-drape', type: 'raster', source: 'planet-drape',
            paint: { 'raster-opacity': 0.88 },
          });
        }
      });
    });
  }

  onLeave(): void {
    if (this.miniMap) {
      this.miniMap.setTerrain(null);
      this.miniMap.remove();
      this.miniMap = null;
    }
    this.overlay?.remove();
    this.overlay = null;
  }
}
